/** The parts of a fetched account this module reads. Structural, so no `@qw/graphql` import. */
interface CachedUserIdentity {
  email?: string | null;
}

/** The parts of a decoded access token this module reads. */
interface CredentialIdentity {
  email?: string | null;
}

/**
 * Whether something computed for one credential still applies to the credential in storage.
 *
 * `unknown` is a distinct answer rather than folded into `different`, because what to do about it
 * depends on the caller:
 *
 * - A *rendering* decision fails open. Blanking a working app because a token was unreadable is a
 *   worse outcome than rendering it, and the server authorizes independently of what we draw. That
 *   is `isCachedUserStale` below, the only caller today.
 * - An *authorization* decision would fail closed: a verdict that cannot be attributed to the
 *   current credential must not be trusted. There is no such caller here now —
 *   `knowledge-base`'s `/api/check-admin` gate was the one, and it left with the two Next.js
 *   provider copies (see B-1968, which reinstates them). The third value is kept because it is
 *   what lets the rendering caller fail open deliberately rather than by accident.
 *
 * Compared on `email`, which is the claim the API authorizes with: `JwtStrategy.validate` calls
 * `getUserAccountByEmail(payload.email)`. Deliberately *not* `sub` — that is the identity
 * provider's id, `UserAccount.id` is a QuoteWell database UUID, and nothing joins them.
 *
 * Case-insensitive, because the two sides come from different places: the token claim is whatever
 * the identity provider minted, and `UserAccount.email` is whatever the database holds.
 */
export type CredentialMatch = "same" | "different" | "unknown";

export function matchCredential(
  computedForEmail: string | null | undefined,
  credentialEmail: string | null | undefined
): CredentialMatch {
  if (!computedForEmail || !credentialEmail) {
    return "unknown";
  }

  return computedForEmail.toLowerCase() === credentialEmail.toLowerCase() ? "same" : "different";
}

/**
 * Whether a cached account has stopped belonging to the credential in storage.
 *
 * `authDataAtom` is an `atomWithStorage`, so it syncs across tabs on the `storage` event, and
 * `authLink` reads the access token out of `localStorage` per operation. A sibling tab signing in
 * as someone else therefore swaps the credential under a tree that is still holding the first
 * account, while `authTokenValidatedAtom` (a plain atom, not synced) stays true — so `AuthGuard`
 * keeps rendering. Without this check, the "a usable `user` beats a failed refetch" branch shows
 * one person's name, and identifies them to LaunchDarkly and PostHog, while every request
 * authorizes as another.
 *
 * A rendering decision, so `unknown` is not stale — see `matchCredential`.
 *
 * Lives here rather than in a provider because all three providers need it: the shared
 * `AccountProvider` and the two hand-rolled `AccountProviderNextJs` copies in `attribution` and
 * `knowledge-base`. Those copies keep diverging from the shared one (B-1968); this is the piece of
 * the logic that can be shared without the framework-boundary refactor, so it is the piece that
 * cannot diverge.
 */
export function isCachedUserStale({
  jwtPayload,
  user,
}: {
  jwtPayload?: CredentialIdentity | null;
  user?: CachedUserIdentity | null;
}): boolean {
  return matchCredential(user?.email, jwtPayload?.email) === "different";
}
